import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import GenericFilterBar from "@/components/GenericFilterBar";
import {
  contextFromRows, contextFromWideRows, contextFromGrouped,
} from "@/analytics/charts/chart.context";
import SimpleChartGrid, { ChartItem } from "@/components/SimpleChartGrid";
import { NoAccess } from "@/components/NoAccess";
import { isForbiddenError } from "@/services/apiClient";
import { ChatDock } from "@/components/ChatDock";
import { MobileTopBar } from "@/components/MobileTopBar";
import UtilisationSkeleton from "@/components/UtilisationSkeleton";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import {
  CORE_PALETTE, getChartColors, AXIS_TICK_STYLE, CHART_MARGIN, formatYAxisLabel, renderMonthYearTick, MONTH_YEAR_AXIS_HEIGHT,
} from "@/analytics/chart.utilities";
import { getDefaultDateRange } from "@/analytics/api";
import {
  fetchLockupFilterOptions,
  fetchLockupDateRange,
  fetchLockupMonthly,
  fetchLockupMonthlyByAgeBand,
  fetchLockupRiskConcentration,
  fetchLockupByOfficeAgeBand,
  fetchLockupByServiceLine,
  fetchLockupByLocation,
  fetchLockupAvgAge,
  LOCKUP_DRILL_FILTER_KEYS,
  type LockupFilters,
  type LockupFilterOptions,
} from "@/analytics/lockup.api";
import { buildDrillThroughParams } from "@/lib/drillThroughTableQuery";
import { makeFilterCache } from "@/lib/filterPersistence";
import { displayAgeBandLabel } from "@/lib/ageBandLabels";
import { useChatVisibility } from "@/hooks/useChatVisibility";

const AGE_BANDS = ["0-30", "31-60", "61-90", "91-120", "120-180", "180+"];
const MONTH_INDEX: Record<string, string> = {
  jan: "01", january: "01",
  feb: "02", february: "02",
  mar: "03", march: "03",
  apr: "04", april: "04",
  may: "05",
  jun: "06", june: "06",
  jul: "07", july: "07",
  aug: "08", august: "08",
  sep: "09", sept: "09", september: "09",
  oct: "10", october: "10",
  nov: "11", november: "11",
  dec: "12", december: "12",
};

const EMPTY_FILTERS: LockupFilters = {
  service_lines: [], legacy_locations: [], managers: [], assignment_categories: [],
  age_band_names: [], date_from: null, date_to: null,
};

const MAX_MONTH_CACHE_KEY = "prism:lockup:max_month";
const FILTERS_STORAGE_KEY = "prism:lockup:filters";
const readCachedMaxMonth = (): string | null => {
  try { return localStorage.getItem(MAX_MONTH_CACHE_KEY); } catch { return null; }
};
const writeCachedMaxMonth = (v: string | null) => {
  try {
    if (v) localStorage.setItem(MAX_MONTH_CACHE_KEY, v);
    else localStorage.removeItem(MAX_MONTH_CACHE_KEY);
  } catch { /* no-op */ }
};

const filterCache = makeFilterCache<LockupFilters>("prism:lockup:filters");

const CHART_KEYS = [
  "monthly", "byAge", "risk", "byOffice", "bySL", "byLoc", "avgAge",
] as const;

const LockupPage = () => {
  const navigate = useNavigate();
  const [opts, setOpts] = useState<LockupFilterOptions>({
    service_lines: [], legacy_locations: [], managers: [], assignment_categories: [], age_band_names: [],
    min_month: null, max_month: null,
  });
  const [filters, setFilters] = useState<LockupFilters>(() => {
    const persisted = filterCache.read();
    if (persisted) return persisted;
    const cached = readCachedMaxMonth();
    if (!cached) return EMPTY_FILTERS;
    const { date_from, date_to } = getDefaultDateRange(null, cached);
    return { ...EMPTY_FILTERS, date_from, date_to };
  });
  const [ready, setReady] = useState(() => readCachedMaxMonth() !== null);

  useEffect(() => { filterCache.write(filters); }, [filters]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [data, setData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [validatedFilterKey, setValidatedFilterKey] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useChatVisibility();
  const abortRef = useRef<AbortController | null>(null);
  const optionsAbortRef = useRef<AbortController | null>(null);
  const hasLoadedOnce = useRef(false);
  const palette = getChartColors().solid;

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("prism:sidebar:open");
      return saved !== null ? saved === "true" : true;
    } catch { return true; }
  });
  const handleSidebarChange = (open: boolean) => {
    setSidebarOpen(open);
    try { localStorage.setItem("prism:sidebar:open", String(open)); } catch { /* no-op */ }
  };

  useEffect(() => {
    Promise.all([fetchLockupFilterOptions(EMPTY_FILTERS), fetchLockupDateRange()])
      .then(([initialOptions, dr]) => {
        writeCachedMaxMonth(dr.max_month);
        const { date_from, date_to } = getDefaultDateRange(dr.min_month, dr.max_month);
        setOpts({ ...initialOptions, min_month: dr.min_month, max_month: dr.max_month });
        setFilters(prev => {
          if (!prev.date_from && !prev.date_to) return { ...prev, date_from, date_to };
          return prev.date_to === date_to ? prev : { ...prev, date_to };
        });
        setReady(true);
      })
      .catch(err => {
        // A total first-load failure used to only log, leaving every card
        // blank with no retry — which reads as "no data" rather than
        // "failed". Mark them all errored so each card offers Retry.
        console.error(err);
        setErrors(Object.fromEntries(CHART_KEYS.map(k => [k, true])));
        setLoadingKeys(new Set());
        setReady(true);
      });
  }, []);

  useEffect(() => {
    const defaults = getDefaultDateRange(opts.min_month, opts.max_month);
    const isDefault = !filters.service_lines.length && !filters.legacy_locations.length && !filters.managers.length && !filters.assignment_categories.length && !filters.age_band_names.length && filters.date_from === defaults.date_from && filters.date_to === defaults.date_to;
    try { isDefault ? localStorage.removeItem(FILTERS_STORAGE_KEY) : localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters)); } catch { /* no-op */ }
  }, [filters, opts.min_month, opts.max_month]);

  useEffect(() => {
    if (!ready) return;
    optionsAbortRef.current?.abort();
    const controller = new AbortController();
    optionsAbortRef.current = controller;
    const requestedKey = getFilterKey(filters);
    setOptionsLoading(true);
    setValidatedFilterKey(null);

    const timeout = window.setTimeout(() => {
      fetchLockupFilterOptions(filters, controller.signal)
        .then(nextOptions => {
          if (controller.signal.aborted) return;
          setOpts(prev => ({ ...nextOptions, min_month: prev.min_month, max_month: prev.max_month }));
          setValidatedFilterKey(requestedKey);
        })
        .catch(err => {
          if (!controller.signal.aborted && err?.name !== "AbortError") {
            console.error(err);
            setValidatedFilterKey(requestedKey);
          }
        })
        .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    }, 250);

    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [filters, ready]);

  const fetcherMap = (f: LockupFilters, signal?: AbortSignal) => ({
    monthly:  () => fetchLockupMonthly(f, signal).then(d => ({ monthly: d.data })),
    byAge:    () => fetchLockupMonthlyByAgeBand(f, signal).then(d => ({ byAge: d.data })),
    risk:     () => fetchLockupRiskConcentration(f, signal).then(d => ({ risk: d.data })),
    byOffice: () => fetchLockupByOfficeAgeBand(f, signal).then(d => ({ byOffice: d.data })),
    bySL:     () => fetchLockupByServiceLine(f, signal).then(d => ({ bySL: d })),
    byLoc:    () => fetchLockupByLocation(f, signal).then(d => ({ byLoc: d })),
    avgAge:   () => fetchLockupAvgAge(f, signal).then(d => ({ avgAge: d.data })),
  });

  useEffect(() => {
    if (!ready || validatedFilterKey !== getFilterKey(filters)) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const { signal } = ctrl;
    setNoAccess(false);

    const isFirst = !hasLoadedOnce.current;
    const fetchers = fetcherMap(filters, signal);

    if (isFirst) {
      setInitialLoading(true);
      Promise.all(CHART_KEYS.map(k => fetchers[k]()))
        .then(results => {
          if (signal.aborted) return;
          const merged = results.reduce((acc, r) => ({ ...acc, ...r }), {} as any);
          setData(merged);
          hasLoadedOnce.current = true;
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) setNoAccess(true);
          else console.error(err);
        })
        .finally(() => {
          if (!signal.aborted) setInitialLoading(false);
        });
      return;
    }

    setLoadingKeys(new Set(CHART_KEYS));
    setErrors({});

    CHART_KEYS.forEach(k => {
      fetchers[k]()
        .then(r => {
          if (signal.aborted) return;
          setData((s: any) => ({ ...s, ...r }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) { setNoAccess(true); return; }
          setErrors(prev => ({ ...prev, [k]: true }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        });
    });
  }, [filters, ready, validatedFilterKey]);

  const retryChart = (key: string) => {
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    setLoadingKeys(prev => new Set([...prev, key]));
    const fetchers = fetcherMap(filters);
    const fn = (fetchers as any)[key];
    if (!fn) return;
    fn()
      .then((r: any) => setData((s: any) => ({ ...s, ...r })))
      .catch(() => setErrors(prev => ({ ...prev, [key]: true })))
      .finally(() => {
        setLoadingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
      });
  };

  const toggle = (key: string, value: string) => {
    setFilters(prev => {
      const cur = (prev as any)[key] as string[];
      return { ...prev, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const clearAll = () => {
    const { date_from, date_to } = getDefaultDateRange(opts.min_month, opts.max_month);
    setFilters({ ...EMPTY_FILTERS, date_from, date_to });
  };

  const defaultRange = getDefaultDateRange(opts.min_month, opts.max_month);
  const hasActive =
    filters.service_lines.length || filters.legacy_locations.length || filters.managers.length ||
    filters.assignment_categories.length || filters.age_band_names.length ||
    filters.date_from !== defaultRange.date_from || filters.date_to !== defaultRange.date_to;

  const items: ChartItem[] = useMemo(() => [
    {
      id: "monthly",
      title: "Total Lockup £ Monthly",
      description: "Total cash tied up in unbilled WIP plus unpaid debt across all clients, tracked month by month.",
      isLoading: loadingKeys.has("monthly"),
      hasError: errors.monthly,
      onRetry: () => retryChart("monthly"),
      getContextData: () => contextFromRows(data.monthly, "monthLabel", { total_lockup_gbp: "Lockup £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey="total_lockup_gbp"
                name="Lockup £"
                fill={CORE_PALETTE.forestGreen}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "byAge",
      title: "Lockup £ by Age Band (Stacked)",
      description: "Monthly lockup split by how long each balance has been tied up, from 0-30 days to 180+.",
      isLoading: loadingKeys.has("byAge"),
      hasError: errors.byAge,
      onRetry: () => retryChart("byAge"),
      getContextData: () => contextFromWideRows(data.byAge, "monthLabel", "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byAge ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {AGE_BANDS.map((b, i) => (
                <Bar key={b} dataKey={b} name={displayAgeBandLabel(b)} stackId="age" fill={palette[i % palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "risk",
      title: "Risk Concentration (>90d % of total)",
      description: "Share of total lockup that has been tied up for more than 90 days — a proxy for cash-flow risk.",
      isLoading: loadingKeys.has("risk"),
      hasError: errors.risk,
      onRetry: () => retryChart("risk"),
      getContextData: () => contextFromRows(data.risk, "monthLabel", { risk_pct: "Over 90 days %" }, "percent"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.risk ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, "percent")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="risk_pct" name="Risk %" stroke={CORE_PALETTE.red} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "byOffice",
      title: "Lockup £ by Location & Age Band (latest month)",
      description: "Latest month's lockup by office location, split into age bands.",
      isLoading: loadingKeys.has("byOffice"),
      hasError: errors.byOffice,
      onRetry: () => retryChart("byOffice"),
      getContextData: () => contextFromWideRows(data.byOffice, "office", "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byOffice ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="office" tick={AXIS_TICK_STYLE} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {AGE_BANDS.map((b, i) => (
                <Bar key={b} dataKey={b} name={displayAgeBandLabel(b)} stackId="age" fill={palette[i % palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "bySL",
      title: "Lockup £ by Service Line over time",
      description: "Monthly lockup trend, one line per service line.",
      isLoading: loadingKeys.has("bySL"),
      hasError: errors.bySL,
      onRetry: () => retryChart("bySL"),
      getContextData: () => contextFromGrouped(data.bySL, "monthLabel", "gbp"),
      render: (h) => {
        const series: string[] = data.bySL?.series ?? [];
        const rows: any[] = data.bySL?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "byLoc",
      title: "Lockup £ by Location over time",
      description: "Monthly lockup trend, one line per office location.",
      isLoading: loadingKeys.has("byLoc"),
      hasError: errors.byLoc,
      onRetry: () => retryChart("byLoc"),
      getContextData: () => contextFromGrouped(data.byLoc, "monthLabel", "gbp"),
      render: (h) => {
        const series: string[] = data.byLoc?.series ?? [];
        const rows: any[] = data.byLoc?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "avgAge",
      title: "Average Lockup Age (days) Monthly",
      description: "Average number of days lockup has been tied up, tracked month by month.",
      isLoading: loadingKeys.has("avgAge"),
      hasError: errors.avgAge,
      onRetry: () => retryChart("avgAge"),
      getContextData: () => contextFromRows(data.avgAge, "monthLabel", { avg_age_days: "Average age (days)" }, "days"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.avgAge ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="avg_age_days" name="Avg Age (days)" stroke={CORE_PALETTE.actionOrange} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
  ], [data, errors, loadingKeys, palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const anyLoading = loadingKeys.size > 0;

  function handleLockupDrillThroughButtonClick() {
    const month = getLockupDrillThroughMonth();
    if (!month) return;
    navigate(buildLockupDrillThroughUrl(month, filters));
  }

  function getLockupDrillThroughMonth() {
    return getLatestVisibleMonth(data.monthly);
  }

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange}>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="Lockup" />
          <main className="flex-1 flex overflow-hidden min-h-0">
            <div className="flex-1 min-w-0 p-2 sm:p-3 space-y-4 sm:space-y-6 overflow-auto">
              {noAccess ? (
                <NoAccess />
              ) : initialLoading ? (
                <UtilisationSkeleton count={items.length} filterCount={5} />
              ) : (
                <>
                  <GenericFilterBar
                    fields={[
                      { key: "service_lines", label: "Service Line", options: [...new Set([...opts.service_lines, ...filters.service_lines])], availableOptions: opts.service_lines, selected: filters.service_lines },
                      { key: "legacy_locations", label: "Location", options: [...new Set([...opts.legacy_locations, ...filters.legacy_locations])], availableOptions: opts.legacy_locations, selected: filters.legacy_locations },
                      { key: "managers", label: "Client Manager", options: [...new Set([...opts.managers, ...filters.managers])], availableOptions: opts.managers, selected: filters.managers, searchable: true },
                      { key: "assignment_categories", label: "Category", options: [...new Set([...opts.assignment_categories, ...filters.assignment_categories])], availableOptions: opts.assignment_categories, selected: filters.assignment_categories },
                      { key: "age_band_names", label: "Age Band", options: [...new Set([...opts.age_band_names, ...filters.age_band_names])], availableOptions: opts.age_band_names, selected: filters.age_band_names, labelFor: displayAgeBandLabel },
                    ]}
                    onToggle={toggle}
                    onClearAll={clearAll}
                    min_month={opts.min_month}
                    max_month={opts.max_month}
                    date_from={filters.date_from}
                    date_to={filters.date_to}
                    onDateChange={(date_from, date_to) => setFilters(prev => ({ ...prev, date_from, date_to }))}
                    isLoading={anyLoading || optionsLoading}
                    hasActiveFilters={!!hasActive}
                  />
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      onClick={handleLockupDrillThroughButtonClick}
                      disabled={!getLockupDrillThroughMonth()}
                    >
                      <Search className="h-4 w-4" />
                      Drill Through
                    </Button>
                  </div>
                  <SimpleChartGrid items={items} />
                </>
              )}
            </div>
            <ChatDock
              open={isAssistantOpen}
              onOpenChange={setIsAssistantOpen}
              chartItems={items}
            />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default LockupPage;

function getMonthStart(row: Record<string, unknown> | null | undefined): string | null {
  if (!row) return null;
  const direct =
    row.month ??
    row.month_start ??
    row.monthStart ??
    row.as_at_date ??
    row.date_from ??
    row.period;

  const directMonth = normalizeMonthValue(direct);
  if (directMonth) return directMonth;

  return normalizeMonthValue(row.monthLabel ?? row.month_label);
}

function normalizeMonthValue(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const trimmed = value.trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-01`;

  const labelMatch = trimmed.match(/^([A-Za-z]+)\s+(\d{4})$/) ?? trimmed.match(/^(\d{4})\s+([A-Za-z]+)$/);
  if (!labelMatch) return null;

  const [monthPart, yearPart] = Number(labelMatch[1]) ? [labelMatch[2], labelMatch[1]] : [labelMatch[1], labelMatch[2]];
  const month = MONTH_INDEX[monthPart.toLowerCase()];
  return month ? `${yearPart}-${month}-01` : null;
}

function getLatestVisibleMonth(rows: unknown): string | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const latest = rows[rows.length - 1];
  return getMonthStart(isRecord(latest) ? latest : null);
}

function buildLockupDrillThroughUrl(month: string, filters: LockupFilters): string {
  const params = buildDrillThroughParams({
    month,
    filters: filters as unknown as Record<string, string[] | string | null | undefined>,
    filterKeys: [...LOCKUP_DRILL_FILTER_KEYS],
  });
  return `/metrics/lockup/drill-through?${params.toString()}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getFilterKey(filters: LockupFilters): string {
  return JSON.stringify(filters);
}
